import { createContext, useContext, useEffect, useRef, useState } from "react";

import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { useRouteMatch } from "react-router-dom";

import { useApi } from "../../hooks/useApi";
import useRedirect from "../../hooks/useRedirect";
import { MeetingDetailsStore } from "../../stores/MeetingDetails.store";
import { useAuth } from "../authContext";
import { useJanusContext } from "../janus/JanusContext";

import useCurrentUserSSE from "./useCurrentUserSSE";
import useJoinMeeting from "./useJoinMeeting";
import useMeetingDetailsSSE from "./useMeetingDetailsSSE";
import useMeetingInitialization from "./useMeetingInitialization";
import useParticipantsSSE from "./useParticipantsSSE";
import useRouteEffects from "./useRouteEffects";
import useUserMediaSetup from "./useUserMediaSetup";

const nonIdSubPaths = ["active", "upcoming", "past", "create"];

const MeetingDetailsContext = createContext(new MeetingDetailsStore());
export const useMeetingDetailsStore = () => useContext(MeetingDetailsContext);

const MeetingDetailsProvider = ({ children }) => {
  const [store] = useState(() => new MeetingDetailsStore());
  const meetingDetailsEventSourceRef = useRef();
  const participantsEventSourceRef = useRef();
  const currentUserEventSourceRef = useRef();

  const meetingDetailsRouteMatch = useRouteMatch("/live/:meetingId");
  const inMeetingDetailsRoute = Boolean(meetingDetailsRouteMatch && !nonIdSubPaths.includes(meetingDetailsRouteMatch.params.meetingId));

  const redirect = useRedirect();
  const { userData } = useAuth();
  const { setJanusConfig, setScreenStreamIds, stopJanus } = useJanusContext();
  const { sendCurrentUserEvent } = useApi();
  // TODO: move states related to meeting from globalContext to here (e.g. screens, streams, players, canvas, etc..)

  // Prepare to meet stream screens once received
  useEffect(() => {
    if (store.meeting?.screens) setScreenStreamIds(store.meeting?.screens);
  }, [store.meeting]);

  useEffect(() => {
    if (inMeetingDetailsRoute && !store.isJoined && store.isLeft) {
      console.log("User left and not joined; redirecting to /live.");
      redirect("/live");
      store.reset();
      store.clearPersistedData();
    }
  }, [store.isJoined, store.isLeft]);

  // Start streaming when user joins
  const startStreaming = async (janusConfigs) => {
    console.log("startStreaming 1");
    if (store.meeting?.status === "started" && store.isJoining && !store.isJoined && store.userMediaPermissionsGranted && janusConfigs) {
      console.log("startStreaming 2");
      setJanusConfig(janusConfigs);
    }
  };

  // Stop Janus when user leaves
  const stopStreaming = async () => {
    stopJanus();
  };

  // Init meeting
  useMeetingInitialization({ inMeetingDetailsRoute, meetingDetailsRouteMatch, store, nonIdSubPaths });

  // Preparing user media data
  useUserMediaSetup({ inMeetingDetailsRoute, store });

  // Join the meeting once media received
  useJoinMeeting({
    inMeetingDetailsRoute,
    store,
    eventSources: {
      meetingDetailsEventSource: meetingDetailsEventSourceRef.current,
      participantsEventSource: participantsEventSourceRef.current,
      currentUserEventSource: currentUserEventSourceRef.current,
    },
  });

  // Handle actions on route change
  useRouteEffects({ inMeetingDetailsRoute, store, nonIdSubPaths });

  useMeetingDetailsSSE({
    meetingDetailsEventSourceRef,
    meeting: store.meeting,
    store,
    sendCurrentUserEvent,
    currentUserAuthData: userData,
  });

  useParticipantsSSE({
    participantsEventSourceRef,
    meeting: store.meeting,
    store,
  });

  useCurrentUserSSE({
    currentUserEventSourceRef,
    meeting: store.meeting,
    store,
    startStreaming,
    stopStreaming,
  });

  const isCreator = toJS(store.meeting)?.createdBy?.uid === userData.userId;
  useEffect(() => {
    if (!toJS(store.meeting)) {
      store.setHasPermission({});
    } else {
      store.setHasPermission({
        "end-meeting": isCreator,
      });
    }
  }, [store.meeting, isCreator]);

  return <MeetingDetailsContext.Provider value={store}>{children}</MeetingDetailsContext.Provider>;
};

export default observer(MeetingDetailsProvider);
